import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InboxFilters, applyInboxFilter, EMPTY_INBOX_FILTER } from "@/components/InboxFilters";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Bot, Briefcase, Check, CheckCheck, Clock, Download, FileText, ImageIcon, Loader2, MessageCircle, Mic, Paperclip, RefreshCw, Search, Send, Sliders, Smile, Square, Trash2, UserRoundCog } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { toast } from "sonner";
import { api, apiError, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { WhatsAppInsights } from "@/components/WhatsAppInsights";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const POLL_MS = 15000;
const SSE_FALLBACK_MS = 45000;

const Ticks = ({ status }) => {
  if (status === "read")
    return <CheckCheck className="h-3.5 w-3.5 text-sky-500" data-testid="wa-tick-read" />;
  if (status === "delivered")
    return <CheckCheck className="h-3.5 w-3.5 text-slate-500" data-testid="wa-tick-delivered" />;
  if (status === "sent")
    return <Check className="h-3.5 w-3.5 text-slate-500" data-testid="wa-tick-sent" />;
  if (status === "sending")
    return <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-testid="wa-tick-sending" />;
  if (status === "failed")
    return <span className="text-[10px] font-semibold text-red-500">failed</span>;
  return <Clock className="h-3 w-3 text-slate-400" data-testid="wa-tick-pending" />;
};

const WINDOW_MS = 24 * 60 * 60 * 1000;
const windowStatus = (lastInboundAt, nowTs) => {
  if (!lastInboundAt) return { open: false, never: true };
  const last = new Date(lastInboundAt).getTime();
  const remaining = WINDOW_MS - (nowTs - last);
  if (remaining <= 0) return { open: false, remaining: 0 };
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  return { open: true, remaining, label: `${h}h ${m}m remaining`, warn: remaining < 3600000 };
};

const openMedia = async (url) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  } catch (e) {
    toast.error("Could not open the file");
  }
};

// Inline voice-note / audio player for incoming WhatsApp audio (ogg/opus + mp3).
// The media endpoint needs auth, so we fetch the blob with the api client and feed an object URL
// to a native <audio> element (gives play/pause/progress for free).
const AudioMessage = ({ url, mime, testid }) => {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const urlRef = useRef(null);

  const fetchAudio = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = mime ? new Blob([data], { type: mime }) : data;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(blob);
      setSrc(urlRef.current);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, [url, mime]);

  useEffect(() => {
    fetchAudio();
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (state === "error") {
    return (
      <div className="mb-1 flex items-center gap-2 rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700"
        data-testid={`wa-audio-error-${testid}`}>
        <span>Couldn't load audio.</span>
        <button className="font-semibold underline" onClick={fetchAudio}
          data-testid={`wa-audio-retry-${testid}`}>Retry</button>
      </div>
    );
  }
  if (state === "loading") {
    return (
      <div className="mb-1 flex items-center gap-2 rounded-md bg-black/5 px-2 py-1.5 text-xs text-slate-500"
        data-testid={`wa-audio-loading-${testid}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading voice message…
      </div>
    );
  }
  return (
    <audio controls preload="metadata" src={src} className="mb-1 h-10 w-56 max-w-full"
      data-testid={`wa-audio-${testid}`} />
  );
};

// Inline image / video player for WhatsApp media. The media endpoint needs auth, so we fetch the
// blob with the api client and feed an object URL to a native element.
const MediaMessage = ({ url, mime, kind, testid }) => {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("loading");
  const urlRef = useRef(null);
  const load = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = mime ? new Blob([data], { type: mime }) : data;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(blob);
      setSrc(urlRef.current);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, [url, mime]);
  useEffect(() => {
    load();
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  if (state === "error")
    return (
      <div className="mb-1 flex items-center gap-2 rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
        <span>Couldn't load {kind}.</span>
        <button className="font-semibold underline" onClick={load}>Retry</button>
      </div>
    );
  if (state === "loading")
    return (
      <div className="mb-1 flex items-center gap-2 rounded-md bg-black/5 px-2 py-1.5 text-xs text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading {kind}…
      </div>
    );
  if (kind === "video")
    return <video controls preload="metadata" src={src} className="mb-1 max-h-72 w-60 max-w-full rounded-md"
      data-testid={`wa-video-${testid}`} />;
  return (
    <img src={src} alt="" onClick={() => window.open(src, "_blank")}
      className="mb-1 max-h-72 w-auto max-w-full cursor-pointer rounded-md"
      data-testid={`wa-image-${testid}`} />
  );
};

const fmtWhen = (s) => {
  if (!s) return "";
  const d = new Date(s);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  }
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    timeZone: "Asia/Kolkata",
  });
};


export const WhatsAppInbox = ({ embedded = false }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = ["network_admin", "ceo"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [inbox, setInbox] = useState([]);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [convo, setConvo] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [rotationOpen, setRotationOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [inboxFilter, setInboxFilter] = useState(EMPTY_INBOX_FILTER);
  const [botOpen, setBotOpen] = useState(false);
  const [botEnabled, setBotEnabled] = useState(true);
  const [botSlots, setBotSlots] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  // Cache loaded conversations by id so re-opening a chat is instant and never re-fetches history.
  const convoCacheRef = useRef({});
  const loadingOlderRef = useRef(false);
  const stickBottomRef = useRef(true);   // whether the next render should snap to the newest message
  const [hasMore, setHasMore] = useState(false);
  // Keep the polling interval reading the CURRENT open chat, not the one captured at mount —
  // otherwise closing a chat that was opened via ?c=<id> gets resurrected 15s later by the poll.
  const activeIdRef = useRef(activeId);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const shownInbox = useMemo(() => applyInboxFilter(inbox, inboxFilter), [inbox, inboxFilter]);

  const loadInbox = useCallback(async () => {
    try {
      const s = (searchRef.current || "").trim();
      const { data } = await api.get(`/whatsapp/inbox${s ? `?search=${encodeURIComponent(s)}` : ""}`);
      setInbox(data);
    } catch (e) {
      /* silent on poll */
    }
  }, []);

  // Debounced search: re-query the server so any conversation is findable even past the list cap.
  useEffect(() => {
    searchRef.current = search;
    const t = setTimeout(() => { loadInbox(); }, 300);
    return () => clearTimeout(t);
  }, [search, loadInbox]);

  const applyConvo = useCallback((id, data, { stick = true } = {}) => {
    convoCacheRef.current[id] = data;
    setHasMore(!!data.has_more);
    if (activeIdRef.current === id) {
      stickBottomRef.current = stick;
      setConvo(data);
    }
  }, []);

  // Mutate the messages of a cached conversation in place (for optimistic send + status updates).
  const patchConvo = useCallback((id, updater, { stick = true } = {}) => {
    const cur = convoCacheRef.current[id];
    if (!cur) return;
    const updated = { ...cur, messages: updater(cur.messages || []) };
    convoCacheRef.current[id] = updated;
    if (activeIdRef.current === id) {
      stickBottomRef.current = stick;
      setConvo(updated);
    }
  }, []);

  // Open a chat: serve instantly from cache if we already loaded it; otherwise fetch only the most
  // recent 20 messages. Never reloads the full history.
  const loadConvo = useCallback(async (id) => {
    if (!id) return;
    const cached = convoCacheRef.current[id];
    if (cached) {
      stickBottomRef.current = true;
      setConvo(cached);
      setHasMore(!!cached.has_more);
      setInbox((prev) => prev.map((c) => (c.customer_id === id ? { ...c, unread: 0 } : c)));
      return;
    }
    try {
      const { data } = await api.get(`/whatsapp/conversations/${id}?limit=20`);
      applyConvo(id, data, { stick: true });
      setInbox((prev) => prev.map((c) => (c.customer_id === id ? { ...c, unread: 0 } : c)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setConvo(null);
      setActiveId(null);
    }
  }, [applyConvo]);

  // Cheap poll for the open chat: pull only messages newer than the last one we have and append.
  const appendNew = useCallback(async (id) => {
    if (!id) return;
    const cur = convoCacheRef.current[id];
    if (!cur) { loadConvo(id); return; }
    const msgs = cur.messages || [];
    const real = msgs.filter((m) => !m._optimistic);
    const lastTs = real.length ? real[real.length - 1].created_at : "";
    try {
      const { data } = await api.get(
        `/whatsapp/conversations/${id}?after=${encodeURIComponent(lastTs || "")}`);
      const freshMsgs = data.messages || [];
      // Drop optimistic bubbles that the server has now echoed back (match by outgoing body),
      // so the real row replaces the placeholder instead of duplicating it.
      const freshOutBodies = new Set(freshMsgs.filter((m) => m.direction === "out").map((m) => m.body));
      const kept = msgs.filter((m) => !(m._optimistic && freshOutBodies.has(m.body)));
      const seen = new Set(kept.map((m) => m.id));
      const fresh = freshMsgs.filter((m) => !seen.has(m.id));
      const updated = { ...cur, customer: data.customer, messages: [...kept, ...fresh] };
      const el = scrollRef.current;
      const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      applyConvo(id, updated, { stick: nearBottom });
    } catch (e) {
      /* silent on poll */
    }
  }, [applyConvo, loadConvo]);

  // Scroll-up paging: fetch the 20 messages just older than the topmost one, prepend, keep position.
  const loadOlder = useCallback(async () => {
    const id = activeIdRef.current;
    const cur = id ? convoCacheRef.current[id] : null;
    if (!id || !cur || !cur.has_more || loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const oldestTs = cur.messages[0]?.created_at || "";
      const { data } = await api.get(
        `/whatsapp/conversations/${id}?before=${encodeURIComponent(oldestTs)}&limit=20`);
      const seen = new Set(cur.messages.map((m) => m.id));
      const older = (data.messages || []).filter((m) => !seen.has(m.id));
      const updated = { ...cur, messages: [...older, ...cur.messages], has_more: data.has_more };
      applyConvo(id, updated, { stick: false });
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevHeight;
      });
    } catch (e) {
      /* silent */
    } finally {
      loadingOlderRef.current = false;
    }
  }, [applyConvo]);

  const onMessagesScroll = useCallback((e) => {
    if (e.currentTarget.scrollTop < 48) loadOlder();
  }, [loadOlder]);

  useEffect(() => {
    api.get("/whatsapp/config").then(({ data }) => setConfigured(data.configured)).catch(() => {});
    loadInbox();

    // Real-time push via Server-Sent Events (webhook → server → here) instead of tight polling.
    // EventSource can't set headers, so the JWT rides as ?token=.
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("vs_token") || "";
    let es = null;
    try {
      es = new EventSource(`${base}/api/whatsapp/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        let data = {};
        try { data = JSON.parse(ev.data); } catch { return; }
        if (!data.type) return;
        loadInbox();
        if (data.customer_id && data.customer_id === activeIdRef.current) {
          appendNew(activeIdRef.current);
        }
      };
      es.onerror = () => { /* browser auto-reconnects per retry hint */ };
    } catch (e) { /* SSE unsupported — fallback poll below covers it */ }

    // Slow safety-net poll in case the SSE connection drops (proxies, sleep, network blips).
    const t = setInterval(() => {
      loadInbox();
      if (activeIdRef.current) appendNew(activeIdRef.current);
    }, SSE_FALLBACK_MS);
    return () => { if (es) es.close(); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the open chat in sync with the URL (?c=<id>) so the browser back/forward buttons open
  // and close the chat correctly and no stale chat view lingers after navigating.
  useEffect(() => {
    setActiveId(params.get("c") || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (activeId) loadConvo(activeId);
    else setConvo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (stickBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [convo?.messages?.length]);

  // Tick every 30s so the 24-hour service-window countdown stays live.
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const openConvo = (id) => {
    setActiveId(id);
    setParams(id ? { c: id } : {}, { replace: true });
  };

  // Send a text reply with optimistic UI: the bubble appears instantly as "sending", then flips to
  // the server status (sent/failed). `retryMsg` re-sends a previously failed optimistic bubble.
  const send = async (retryMsg = null) => {
    const body = retryMsg ? retryMsg.body : reply.trim();
    const id = activeId;
    if (!body || !id) return;
    const tempId = retryMsg ? retryMsg.id : `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    if (retryMsg) {
      patchConvo(id, (msgs) => msgs.map((m) => (m.id === tempId ? { ...m, status: "sending" } : m)));
    } else {
      patchConvo(id, (msgs) => [...msgs, {
        id: tempId, direction: "out", body, kind: "text",
        status: "sending", created_at: new Date().toISOString(), _optimistic: true,
      }]);
      setReply("");
      setShowEmoji(false);
    }
    setSending(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${id}/send`, { body });
      // Reflect the server status on the optimistic bubble (keep it optimistic so the SSE/append
      // step can reconcile it with the real server row by matching body — avoids id-swap races).
      patchConvo(id, (msgs) => msgs.map((m) => (
        m.id === tempId ? { ...m, status: data.status || "sent" } : m)), { stick: false });
      if (data.status === "not_configured")
        toast.warning("Reply saved. It will be delivered once WhatsApp credentials are connected.");
      else if (data.status === "failed") toast.error("WhatsApp send failed — tap the message to retry.");
      loadInbox();
    } catch (e) {
      patchConvo(id, (msgs) => msgs.map((m) => (m.id === tempId ? { ...m, status: "failed" } : m)), { stick: false });
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const convertToJob = async () => {
    if (!activeId) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${activeId}/convert`);
      toast.success(data.existing ? "Opening existing job" : "Job created from this lead");
      nav(`/jobs/${data.job_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setConverting(false);
    }
  };

  const openRotation = async () => {
    try {
      const [ag, s] = await Promise.all([
        api.get("/whatsapp/agents"),
        api.get("/whatsapp/bot/settings"),
      ]);
      setAgents(ag.data);
      setBotEnabled(s.data.enabled);
      setBotSlots(s.data.slots || 1);
      setRotationOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openAssign = async () => {
    // Visible confirmation that the click registered (requested for click-through verification).
    toast("Reassign clicked");
    try {
      const { data } = await api.get("/whatsapp/assignable-agents");
      setAgents(data);
      setAssignOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doAssign = async (agentId) => {
    if (!activeId || !agentId) return;
    const agent = agents.find((a) => a.id === agentId);
    try {
      await api.patch(`/whatsapp/conversations/${activeId}/assign`, { cs_person_id: agentId });
      toast.success(`Conversation reassigned${agent ? ` to ${agent.name}` : ""}`);
      setAssignOpen(false);
      // The conversation now belongs to the selected person and leaves this user's view.
      openConvo(null);
      loadInbox();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const [reactivating, setReactivating] = useState(false);
  const reactivateBot = async () => {
    if (!activeId) return;
    setReactivating(true);
    try {
      await api.post(`/whatsapp/conversations/${activeId}/reactivate-bot`);
      toast.success("Bot reactivated — it's reading the chat and will reply shortly");
      setConvo((prev) => (prev ? { ...prev, customer: { ...prev.customer, bot_active: true } } : prev));
      const cached = convoCacheRef.current[activeId];
      if (cached) convoCacheRef.current[activeId] = { ...cached, customer: { ...cached.customer, bot_active: true } };
      loadInbox();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setReactivating(false);
    }
  };

  const setTag = async (tag) => {
    if (!activeId) return;
    try {
      await api.patch(`/whatsapp/conversations/${activeId}/tag`, { lead_tag: tag });
      setConvo((prev) => (prev ? { ...prev, customer: { ...prev.customer, lead_tag: tag } } : prev));
      const cached = convoCacheRef.current[activeId];
      if (cached) convoCacheRef.current[activeId] = { ...cached, customer: { ...cached.customer, lead_tag: tag } };
      loadInbox();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const patchAgent = async (id, patch) => {
    try {
      await api.patch(`/whatsapp/agents/${id}`, patch);
      setAgents((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doDeleteConversation = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/whatsapp/conversations/${deleteTarget.customer_id}`);
      setInbox((prev) => prev.filter((x) => x.customer_id !== deleteTarget.customer_id));
      if (activeId === deleteTarget.customer_id) openConvo(null);
      toast.success("Conversation deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const openBot = async () => {
    try {
      const [s, ag] = await Promise.all([
        api.get("/whatsapp/bot/settings"),
        api.get("/whatsapp/agents"),
      ]);
      setBotEnabled(s.data.enabled);
      setBotSlots(s.data.slots || 1);
      setAgents(ag.data);
      setBotOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleBot = async (enabled) => {
    setBotEnabled(enabled);
    try {
      await api.put("/whatsapp/bot/settings", { enabled });
      toast.success(enabled ? "AI bot added to rotation" : "AI bot removed from rotation");
    } catch (e) {
      setBotEnabled(!enabled);
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveBotSlots = async (slots) => {
    const n = Math.max(1, Math.min(10, Number(slots) || 1));
    setBotSlots(n);
    try {
      await api.put("/whatsapp/bot/settings", { slots: n });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const uploadBlob = async (fileOrBlob, filename) => {
    if (!activeId) return;
    if (!fileOrBlob || !fileOrBlob.size) {
      toast.error("Nothing was recorded. Please hold the mic a moment longer and try again.");
      return;
    }
    const fd = new FormData();
    fd.append("file", fileOrBlob, filename);
    setUploading(true);
    try {
      // Let the browser set multipart/form-data WITH its boundary — setting it manually
      // (without a boundary) breaks body parsing on some Android/mobile stacks.
      const { data } = await api.post(`/whatsapp/conversations/${activeId}/send-media`, fd);
      if (data.status === "not_configured")
        toast.warning("Attachment saved. It will send once WhatsApp is connected.");
      else if (data.status === "failed") toast.error("Attachment send failed.");
      else toast.success("Attachment sent");
      stickBottomRef.current = true;
      loadConvo(activeId);
      loadInbox();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !activeId) return;
    await uploadBlob(f, f.name);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mimeType the device actually supports. Android Chrome can return an empty/
      // unsupported default and yield a corrupt blob, so choose explicitly.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
        "",
      ];
      const picked =
        candidates.find(
          (t) => t === "" || (window.MediaRecorder?.isTypeSupported?.(t) ?? false)
        ) ?? "";
      const rec = picked ? new MediaRecorder(stream, { mimeType: picked }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => ev.data && ev.data.size && chunksRef.current.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || picked || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        await uploadBlob(blob, `voice-note.${ext}`);
      };
      recRef.current = rec;
      // Use a timeslice so chunks flush periodically — some Android devices never emit
      // a final chunk otherwise, producing an empty recording.
      rec.start(500);
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied or unavailable");
    }
  };

  const stopRecording = (cancel = false) => {
    const rec = recRef.current;
    if (!rec) return;
    if (cancel) rec.onstop = () => rec.stream?.getTracks().forEach((t) => t.stop());
    try { rec.stop(); } catch (e) { /* noop */ }
    recRef.current = null;
    setRecording(false);
  };

  return (
    <div className={embedded ? "" : ""} data-testid="whatsapp-inbox">
      {!embedded && (
        <PageHead
          title="WhatsApp Inbox"
          subtitle="B2C leads from WhatsApp — reply directly to customers"
        >
          {isManager && (
            <>
              <Button variant="outline" onClick={openBot} data-testid="wa-bot-settings-btn">
                <Bot className="mr-2 h-4 w-4" /> AI bot
              </Button>
              <Button variant="outline" onClick={openRotation} data-testid="wa-rotation-btn">
                <Sliders className="mr-2 h-4 w-4" /> Rotation
              </Button>
            </>
          )}
        </PageHead>
      )}
      {embedded && isManager && (
        <div className="mb-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={openBot} data-testid="wa-bot-settings-btn">
            <Bot className="mr-2 h-4 w-4" /> AI bot
          </Button>
          <Button variant="outline" size="sm" onClick={openRotation} data-testid="wa-rotation-btn">
            <Sliders className="mr-2 h-4 w-4" /> Rotation
          </Button>
        </div>
      )}
      {isManager && <WhatsAppInsights />}
      {!configured && (
        <div
          className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="wa-not-configured-banner"
        >
          WhatsApp isn't connected yet. Incoming messages still create leads and your replies are
          saved — they'll send to customers once the Meta credentials are added.
        </div>
      )}
      <div>
        {/* Conversation list (full width; the open chat covers it as a full-screen overlay) */}
        <div
          className="rounded-lg border border-brand-line bg-white"
          data-testid="wa-conversation-list"
        >
          <div className="flex items-center justify-between border-b border-brand-line px-3 py-2">
            <span className="text-sm font-semibold text-brand-ink">Conversations</span>
            <button
              onClick={loadInbox}
              className="rounded p-1 text-slate-400 hover:text-brand"
              data-testid="wa-refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="border-b border-brand-line px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or number…"
                className="h-9 pl-8 text-sm"
                data-testid="wa-search-input"
              />
            </div>
          </div>
          <InboxFilters value={inboxFilter} onChange={setInboxFilter} count={shownInbox.length} total={inbox.length}
            agents={[...new Set(inbox.map((c) => c.cs_person_name).filter(Boolean))].sort()} />
          <div className="max-h-[70vh] divide-y divide-slate-100 overflow-y-auto">
            {shownInbox.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-slate-400" data-testid="wa-inbox-empty">
                {search.trim() || inbox.length ? "No conversations match your search or filters." : "No WhatsApp leads yet."}
              </p>
            )}
            {shownInbox.map((c) => (
              <div
                key={c.customer_id}
                className="group relative flex w-full items-start gap-2 px-3 py-3 transition-colors hover:bg-brand-tint/50"
                data-testid={`wa-convo-row-${c.customer_id}`}
              >
                <button
                  onClick={() => openConvo(c.customer_id)}
                  className="flex flex-1 items-start gap-2 text-left"
                  data-testid={`wa-convo-item-${c.customer_id}`}
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <MessageCircle className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-brand-ink">{c.name}</span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {c.last_ts && (
                          <span
                            className="text-[10px] text-slate-400"
                            data-testid={`wa-convo-time-${c.customer_id}`}
                          >
                            {fmtWhen(c.last_ts)}
                          </span>
                        )}
                        {c.unread > 0 && (
                          <Badge className="border-0 bg-emerald-600 px-1.5 text-[10px] text-white">
                            {c.unread}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="truncate text-xs text-slate-500">{c.last_message || "—"}</p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <p className="truncate text-[10px] text-slate-400">
                        {c.wa_id}
                        {c.cs_person_name ? ` · ${c.cs_person_name}` : ""}
                      </p>
                      {c.bot_active && (
                        <span
                          className="shrink-0 rounded-full bg-brand-tint px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand"
                          data-testid={`wa-convo-bot-badge-${c.customer_id}`}
                        >
                          Bot
                        </span>
                      )}
                      {c.lead_tag === "hot" && (
                        <span
                          className="shrink-0 rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
                          data-testid={`wa-convo-hot-badge-${c.customer_id}`}
                        >
                          🔥 Hot Lead
                        </span>
                      )}
                      {c.lead_tag === "no_response" && (
                        <span
                          className="shrink-0 rounded-full bg-slate-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
                          data-testid={`wa-convo-noresponse-badge-${c.customer_id}`}
                        >
                          No Response
                        </span>
                      )}
                      {c.booked && (
                        <span
                          className="shrink-0 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
                          data-testid={`wa-convo-booked-badge-${c.customer_id}`}
                        >
                          Booked
                        </span>
                      )}
                      {!c.has_job && (
                        <span
                          className="shrink-0 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-950"
                          data-testid={`wa-convo-no-job-badge-${c.customer_id}`}
                        >
                          No Job Created
                        </span>
                      )}
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(c);
                  }}
                  title="Delete conversation"
                  aria-label="Delete conversation"
                  className="mt-0.5 shrink-0 rounded p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                  data-testid={`wa-convo-delete-${c.customer_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Full-screen WhatsApp chat — nothing else on screen when a conversation is open */}
        {convo && createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col"
            style={{ backgroundColor: "#efeae2" }}
            data-testid="wa-chat-pane"
          >
            {/* Contact header */}
            <div className="flex items-center gap-3 bg-[#008069] px-3 py-2 text-white">
              <button onClick={() => openConvo(null)} data-testid="wa-back-btn">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold leading-tight">{convo.customer.name}</p>
                  {!convo.customer.has_job && (
                    <span
                      className="shrink-0 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950"
                      data-testid="wa-no-job-badge"
                    >
                      No Job Created
                    </span>
                  )}
                </div>
                <p className="truncate text-xs leading-tight text-white/80">
                  {convo.customer.wa_id}
                  {(() => {
                    const outs = convo.messages.filter((m) => m.direction === "out");
                    const last = outs[outs.length - 1];
                    const label = last
                      ? { read: "✓✓ Read", delivered: "✓✓ Delivered", sent: "✓ Sent",
                          failed: "⚠ Failed", not_configured: "pending" }[last.status] || ""
                      : "";
                    return label ? ` · ${label}` : "";
                  })()}
                  {convo.customer.cs_person_name ? ` · ${convo.customer.cs_person_name}` : ""}
                </p>
                <select
                  value={convo.customer.lead_tag || ""}
                  onChange={(e) => setTag(e.target.value)}
                  data-testid="wa-lead-tag-select"
                  className="mt-1 rounded-md border border-white/30 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white outline-none"
                >
                  <option className="text-slate-900" value="">No tag</option>
                  <option className="text-slate-900" value="hot">🔥 Hot Lead</option>
                  <option className="text-slate-900" value="no_response">No Response</option>
                </select>
              </div>
              {!convo.customer.bot_active && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-white hover:bg-white/15 hover:text-white"
                  onClick={reactivateBot}
                  disabled={reactivating}
                  data-testid="wa-reactivate-bot-btn"
                >
                  {reactivating ? (
                    <Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" />
                  ) : (
                    <Bot className="h-4 w-4 sm:mr-1.5" />
                  )}
                  <span className="hidden sm:inline">Reactivate Bot</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 text-white hover:bg-white/15 hover:text-white"
                onClick={openAssign}
                data-testid="wa-reassign-btn"
              >
                <UserRoundCog className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Reassign</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 text-white hover:bg-white/15 hover:text-white"
                onClick={convertToJob}
                disabled={converting}
                data-testid="wa-convert-job-btn"
              >
                <Briefcase className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Create job</span>
              </Button>
            </div>

            {/* Messages on WhatsApp doodle background */}
            <div
              ref={scrollRef}
              onScroll={onMessagesScroll}
              className="flex-1 space-y-1.5 overflow-y-auto px-3 py-3"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Ccircle cx='16' cy='16' r='3'/%3E%3Ccircle cx='56' cy='36' r='2.5'/%3E%3Crect x='36' y='60' width='6' height='6' rx='1'/%3E%3Cpath d='M64 8l3 5h-6z'/%3E%3C/g%3E%3C/svg%3E\")",
              }}
              data-testid="wa-messages"
            >
              {hasMore && (
                <div className="flex justify-center py-1">
                  <button
                    onClick={loadOlder}
                    data-testid="wa-load-older-btn"
                    className="rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-slate-500 shadow-sm hover:text-brand"
                  >
                    Load earlier messages
                  </button>
                </div>
              )}
              {convo.messages.map((m) => {
                const out = m.direction === "out";
                return (
                  <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                    <div
                      onClick={() => out && m.status === "failed" && m.body && send(m)}
                      className={`max-w-[80%] rounded-lg px-2 py-1.5 text-sm shadow-sm ${
                        out ? "rounded-tr-none bg-[#d9fdd3] text-slate-800" : "rounded-tl-none bg-white text-slate-800"
                      } ${out && m.status === "failed" ? "cursor-pointer ring-1 ring-red-300" : ""}`}
                      data-testid={out && m.status === "failed" ? `wa-retry-${m.id}` : undefined}
                    >
                      {["audio", "voice"].includes(m.kind) && m.media_url ? (
                        <AudioMessage url={m.media_url} mime={m.media_mime} testid={m.id} />
                      ) : m.kind === "image" && m.media_url ? (
                        <MediaMessage url={m.media_url} mime={m.media_mime} kind="image" testid={m.id} />
                      ) : m.kind === "video" && m.media_url ? (
                        <MediaMessage url={m.media_url} mime={m.media_mime} kind="video" testid={m.id} />
                      ) : m.kind && m.kind !== "text" ? (
                        <button
                          className={`mb-1 flex items-center gap-2 rounded-md bg-black/5 px-2 py-1.5 text-left ${
                            m.media_url ? "hover:bg-black/10" : "cursor-default"
                          }`}
                          onClick={() => m.media_url && openMedia(m.media_url)}
                          data-testid={`wa-media-${m.id}`}
                        >
                          {m.kind === "image" ? (
                            <ImageIcon className="h-4 w-4 shrink-0" />
                          ) : (
                            <FileText className="h-4 w-4 shrink-0" />
                          )}
                          <span className="truncate">
                            {m.media_filename ||
                              (m.kind === "image" ? "Photo" : m.kind === "document" ? "Document" : m.kind)}
                            {!m.media_url ? " (pending)" : ""}
                          </span>
                          {m.media_url && <Download className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                        </button>
                      ) : null}
                      {m.body ? <p className="whitespace-pre-wrap break-words">{m.body}</p> : null}
                      <span className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-slate-500">
                        {out && m.status === "failed" && (
                          <span className="mr-1 inline-flex items-center gap-0.5 font-semibold text-red-500">
                            <RefreshCw className="h-3 w-3" /> tap to retry
                          </span>
                        )}
                        {fmtTime(m.created_at)}
                        {out && <Ticks status={m.status} />}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 24-hour service window */}
            {(() => {
              const win = windowStatus(convo.customer.last_inbound_at, nowTs);
              if (win.open) {
                return (
                  <div
                    className={`flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium ${
                      win.warn ? "bg-orange-100 text-orange-700" : "bg-emerald-50 text-emerald-700"
                    }`}
                    data-testid="wa-window-timer"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    24hr window: {win.label}
                  </div>
                );
              }
              return (
                <div
                  className="flex items-center justify-center gap-2 bg-rose-100 px-3 py-1.5 text-xs font-medium text-rose-700"
                  data-testid="wa-window-closed"
                >
                  <Clock className="h-3.5 w-3.5" />
                  Window closed — use a template message
                </div>
              );
            })()}

            {/* Input bar */}
            {(() => {
              const win = windowStatus(convo.customer.last_inbound_at, nowTs);
              const closed = !win.open;
              return (
                <div className="relative flex items-end gap-2 bg-[#f0f2f5] px-2 py-2">
                  {showEmoji && !closed && (
                    <>
                      <div className="fixed inset-0 z-[9]" onClick={() => setShowEmoji(false)}
                        data-testid="wa-emoji-backdrop" />
                      <div className="absolute bottom-16 left-2 z-10" data-testid="wa-emoji-picker">
                        <EmojiPicker
                          onEmojiClick={(ev) => setReply((r) => r + ev.emoji)}
                          height={360}
                          width={300}
                          previewConfig={{ showPreview: false }}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex flex-1 items-center gap-1 rounded-full bg-white px-3 py-1.5 shadow-sm">
                    <button
                      onClick={() => setShowEmoji((s) => !s)}
                      disabled={closed}
                      className="shrink-0 text-slate-500 hover:text-slate-700 disabled:opacity-40"
                      data-testid="wa-emoji-btn"
                    >
                      <Smile className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading || closed}
                      className="shrink-0 text-slate-500 hover:text-slate-700 disabled:opacity-40"
                      data-testid="wa-attach-btn"
                    >
                      <Paperclip className="h-5 w-5" />
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                      className="hidden"
                      onChange={handleFile}
                      data-testid="wa-file-input"
                    />
                    <input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                      placeholder={closed ? "Window closed — use a template" : "Message"}
                      disabled={closed}
                      className="min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                      data-testid="wa-reply-input"
                    />
                  </div>
                  {reply.trim() && !closed ? (
                    <button
                      onClick={() => send()}
                      disabled={sending || uploading}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#008069] text-white shadow-sm disabled:opacity-60"
                      data-testid="wa-send-btn"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  ) : recording ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => stopRecording(true)}
                        className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-300 text-slate-700 shadow-sm"
                        data-testid="wa-record-cancel"
                        title="Cancel"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => stopRecording(false)}
                        className="flex h-11 w-11 animate-pulse items-center justify-center rounded-full bg-red-500 text-white shadow-sm"
                        data-testid="wa-record-stop"
                        title="Stop & send"
                      >
                        <Square className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={startRecording}
                      disabled={uploading || closed}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#008069] text-white shadow-sm disabled:opacity-60"
                      data-testid="wa-record-btn"
                      title="Record voice note"
                    >
                      <Mic className="h-5 w-5" />
                    </button>
                  )}
                </div>
              );
            })()}
          </div>,
          document.body
        )}
      </div>

      <Dialog open={rotationOpen} onOpenChange={setRotationOpen}>
        <DialogContent className="sm:max-w-md" data-testid="wa-rotation-dialog">
          <DialogHeader>
            <DialogTitle>Lead rotation</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            New WhatsApp leads are shared out by weight (higher = more leads). Pause an agent to keep
            them out of the rotation. The AI Sales Bot is one of the agents — give it slots too.
          </p>
          <div className="mt-2 space-y-2">
            <div
              className="flex items-center gap-3 rounded-md border border-brand-ink/20 bg-brand-tint/40 p-2"
              data-testid="wa-rotation-bot-row"
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-medium text-brand-ink">
                  <Bot className="h-3.5 w-3.5" /> AI Sales Bot
                </p>
                <p className="text-[11px] text-slate-400">handles leads automatically</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500">Slots</span>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={botSlots}
                  disabled={!botEnabled}
                  className="h-8 w-14"
                  data-testid="wa-rotation-bot-slots"
                  onChange={(e) => saveBotSlots(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500">{botEnabled ? "Active" : "Off"}</span>
                <Switch
                  checked={botEnabled}
                  data-testid="wa-rotation-bot-switch"
                  onCheckedChange={toggleBot}
                />
              </div>
            </div>
            {agents.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 rounded-md border border-brand-line p-2"
                data-testid={`wa-agent-row-${a.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-brand-ink">{a.name}</p>
                  <p className="text-[11px] text-slate-400">{a.load} leads assigned</p>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-slate-500">Weight</span>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={a.weight}
                    disabled={a.paused}
                    className="h-8 w-14"
                    data-testid={`wa-agent-weight-${a.id}`}
                    onChange={(e) => patchAgent(a.id, { weight: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-slate-500">{a.paused ? "Paused" : "Active"}</span>
                  <Switch
                    checked={!a.paused}
                    data-testid={`wa-agent-pause-${a.id}`}
                    onCheckedChange={(v) => patchAgent(a.id, { paused: !v })}
                  />
                </div>
              </div>
            ))}
            {agents.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-400">No Central Sales agents found.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={botOpen} onOpenChange={setBotOpen}>
        <DialogContent className="sm:max-w-md" data-testid="wa-bot-dialog">
          <DialogHeader>
            <DialogTitle>AI sales bot</DialogTitle>
          </DialogHeader>
          <div
            className="flex items-center justify-between rounded-md border border-brand-line bg-brand-tint/40 p-3"
            data-testid="wa-bot-master-row"
          >
            <div className="min-w-0 pr-3">
              <p className="text-sm font-medium text-brand-ink">
                {botEnabled ? "Bot is ON" : "Bot is paused"}
              </p>
              <p className="text-[11px] text-slate-500">
                When ON, brand-new WhatsApp numbers are answered automatically by the AI closer.
              </p>
            </div>
            <Switch
              checked={botEnabled}
              data-testid="wa-bot-master-switch"
              onCheckedChange={toggleBot}
            />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Availability — a bot lead that goes quiet for 30 minutes is auto-assigned (round-robin) to
            one available agent. Switch an agent off to keep them out of auto-handoffs.
          </p>
          <div className="mt-2 space-y-2">
            {agents.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 rounded-md border border-brand-line p-2"
                data-testid={`wa-bot-agent-row-${a.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-brand-ink">{a.name}</p>
                  <p className="text-[11px] text-slate-400">{a.load} leads assigned</p>
                </div>
                <span className="text-[11px] text-slate-500">
                  {a.available === false ? "Unavailable" : "Available"}
                </span>
                <Switch
                  checked={a.available !== false}
                  data-testid={`wa-bot-agent-available-${a.id}`}
                  onCheckedChange={(v) => patchAgent(a.id, { available: v })}
                />
              </div>
            ))}
            {agents.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-400">No Central Sales agents found.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="z-[120] sm:max-w-sm" data-testid="wa-assign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign lead</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Move this conversation to another Central Sales agent, or hand it back to the AI Sales
            Bot. The bot stops the moment a human takes over.
          </p>
          <div className="mt-2 space-y-1">
            {agents.filter((a) => !a.paused && a.available !== false).map((a) => {
              const botCurrent =
                a.is_bot && convo && convo.customer.bot_active && !convo.customer.cs_person_name;
              const isCurrent = botCurrent || (convo && a.name === convo.customer.cs_person_name);
              return (
                <button
                  key={a.id}
                  onClick={() => doAssign(a.id)}
                  disabled={isCurrent}
                  className="flex w-full items-center justify-between rounded-md border border-brand-line px-3 py-2 text-left text-sm transition-colors hover:bg-brand-tint/50 disabled:opacity-50"
                  data-testid={`wa-assign-agent-${a.id}`}
                >
                  <span className="font-medium text-brand-ink">
                    {a.name}
                    {a.is_bot && (
                      <span className="ml-2 rounded bg-brand-tint px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand">
                        Bot
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {`${a.load} leads`}
                    {isCurrent ? " · current" : ""}
                  </span>
                </button>
              );
            })}
            {agents.filter((a) => !a.paused && a.available !== false).length === 0 && (
              <p className="py-4 text-center text-sm text-slate-400">No available agents.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="wa-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the chat with{" "}
              <span className="font-semibold text-brand-ink">
                {deleteTarget?.name || deleteTarget?.wa_id}
              </span>{" "}
              and its full message history from the inbox. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wa-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doDeleteConversation();
              }}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="wa-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WhatsAppInbox;
